/**
 * A module used at the top of the HourOfCodePage
 */

import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {cssUrl} from "@repo/urls";

import * as mq from "#marketing-content-shared-package/media-queries.ts";
import {SerifHeadingHero} from "#marketing-content-shared-package/typography/typography.ts";

import emailIcon from "@phosphor-icons/core/regular/envelope-simple.svg";
import facebookIcon from "@phosphor-icons/core/regular/facebook-logo.svg";
import twitterIcon from "@phosphor-icons/core/regular/twitter-logo.svg";

type Props = {
    kind: "Hero";
};

export default class Hero extends React.Component<Props> {
    render(): React.ReactNode {
        const img = "/images/hour_of_code/hero_background.png";
        return (
            <View style={styles.heroWrapper}>
                <Spring />
                <View style={styles.hero}>
                    <View style={styles.left}>
                        <div
                            className={css(styles.image)}
                            style={{
                                backgroundImage: cssUrl(img),
                            }}
                        />
                    </View>
                    <View style={styles.right}>
                        <SerifHeadingHero tag="h1" style={styles.title}>
                            <Trans>
                                Try an Hour of Code™
                                <br />
                                with Khan Academy
                            </Trans>
                        </SerifHeadingHero>
                        <View style={styles.ctaBtnWrapper}>
                            <Button
                                style={styles.ctaBtn}
                                href="/computing/hour-of-code/hour-of-code-lessons/hour-of-drawing-code/v/welcome-hour-of-code"
                            >
                                {t`Start coding now`}
                            </Button>
                            <Button
                                kind="secondary"
                                style={styles.ctaBtn}
                                href="/computing/hour-of-code/hour-of-code-resources/hour-of-code-for-teachers/a/using-hour-of-code-in-your-classroom"
                            >
                                {t`Teachers, start here`}
                            </Button>
                            <Button
                                kind="secondary"
                                style={styles.ctaBtn}
                                href="/computing/hour-of-code/hour-of-code-resources/hour-of-code-for-teachers/a/using-hour-of-code-with-your-child"
                            >
                                {t`Parents, start here`}
                            </Button>
                        </View>
                        <View style={styles.socialBtnWrapper}>
                            <IconButton
                                icon={facebookIcon}
                                kind="tertiary"
                                aria-label={t`Facebook`}
                                href="https://www.facebook.com/dialog/share?app_id=160249463991765&display=page&href=https%3A%2F%2Fwww.khanacademy.org%2Fhourofcode&redirect_uri=https%3A%2F%2Fwww.khanacademy.org"
                            />
                            <Strut size={16} />
                            <IconButton
                                icon={twitterIcon}
                                kind="tertiary"
                                aria-label={t`Twitter`}
                                href="https://twitter.com/intent/tweet?hashtags=hourofcode&text=Try+an+hour+of+code+on+%40KhanAcademy+-+JS%2C+HTML%2C+or+SQL%21&url=https%3A%2F%2Fkhanacademy.org%2Fhourofcode"
                            />
                            <Strut size={16} />
                            <IconButton
                                icon={emailIcon}
                                kind="tertiary"
                                aria-label={t`Email`}
                                href="mailto:?Subject=Try%20an%20Hour%20Of%20Code&amp;Body=Anyone%20can%20learn%20to%20code.%20Try%20an%20hour%20of%20code%20on%20Khan%20Academy%20-%20JS,%20HTML,%20or%20SQL!%20https%3A//khanacademy.org/hourofcode"
                            />
                        </View>
                    </View>
                </View>
                <Spring />
            </View>
        );
    }
}

const BG_COLOR = "#CFEDF7";
const PAGE_WIDTH = 1000;

const styles = StyleSheet.create({
    heroWrapper: {
        backgroundColor: BG_COLOR,
        width: "100%",
        alignItems: "center",
        flexDirection: "row",
    },
    hero: {
        flexBasis: PAGE_WIDTH,
        maxWidth: PAGE_WIDTH,
        flexDirection: "row",
        justifyContent: "flex-end",
        [mq.smallDesktopOrSmaller]: {
            marginLeft: 24,
            marginRight: 24,
        },
        [mq.smallTabletOrSmaller]: {
            flexDirection: "column",
        },
    },
    left: {
        marginTop: 48,
        marginBottom: 48,
        marginRight: 40,
        [mq.smallTabletOrSmaller]: {
            display: "none",
        },
    },
    image: {
        width: 850,
        height: 500,
        marginLeft: -400,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        [mq.smallDesktopOrSmaller]: {
            width: 750,
            height: 400,
            marginLeft: -300,
        },
        [mq.tabletOrSmaller]: {
            width: 550,
            height: 300,
            marginLeft: -300,
        },
    },
    right: {
        paddingRight: 16,
        alignSelf: "center",
        minWidth: 480,
        [mq.smallDesktopOrSmaller]: {
            minWidth: 320,
        },
        [mq.tabletOrSmaller]: {
            minWidth: 460,
            marginTop: 48,
            marginBottom: 53,
        },
        [mq.smallTabletOrSmaller]: {
            maxWidth: "100%",
            textAlign: "center",
        },
    },
    title: {
        color: color.darkBlue,
    },
    ctaBtn: {
        minWidth: 191,
        marginBottom: 23,
    },
    ctaBtnWrapper: {
        marginTop: 32,
        alignItems: "flex-start",
        [mq.smallTabletOrSmaller]: {
            alignItems: "center",
        },
    },
    socialBtnWrapper: {
        flexDirection: "row",
        [mq.smallTabletOrSmaller]: {
            justifyContent: "center",
        },
    },
});
